// Events plugin.
import pluginInterface from './interface'
import { emitEvent } from './interface/actions'
import * as actionTypes from './interface/actionTypes'

import eventEmitter from './eventEmitter'

// Logs
import { logManager } from '../logs'
const log = logManager.getLogger('EVENTS')

/**
 * Event Emitter Implementation.
 * Factory function to create the Event Emitter Implementation.
 * Defines the functionality exposed by an Event Emitter Interface.
 */
export default function eventsImplementation (options = {}, bottle) {
  // Create the emitter and provide it to the middleware.
  //    It is created here so that it can also be provided to the bottle.
  const emitter = eventEmitter()
  const middleware = setupMiddleware(emitter)

  // Provide the emit function to the bottle container.
  //    This function will be provided to other plugins for emitting events.
  bottle.value('emitEvent', function emitEvent (type, args) {
    log.info(`Emitting event: ${type}`, args)
    emitter.emit(type, args)
  })

  return {
    name: pluginInterface.name,
    middleware: middleware,
    api: pluginInterface.api,
    reducer: pluginInterface.reducer
  }
}

/*
 * Extra wrapper function for the middleware so that we can provide an already
 *     created emitter to it.
 */
function setupMiddleware (emitter) {
  /**
   * Implementation of Event Emitter Interface.
   * @return {Middleware} middleware Middleware to be applied to the redux store.
   */
  return function middleware (context) {
    var eventMap = {}

    /**
     * Middleware to handle Event Emitter Interface actions.
     * Redirects Interface actions to the Plugin functionality, and dispatches
     *      actions to update the store if needed.
     * @type {Function}
     */
    return next => action => {
      switch (action.type) {
        case actionTypes.EVENTS_ON:
          emitter.on(action.payload.eventType, action.payload.listener)
          break
        case actionTypes.EVENTS_OFF:
          emitter.off(action.payload.eventType, action.payload.listener)
          break
        case actionTypes.EVENTS_SUBSCRIBE:
          emitter.subscribe(action.payload)
          break
        case actionTypes.EVENTS_UNSUBSCRIBE:
          emitter.unsubscribe(action.payload)
          break
        case actionTypes.EVENTS_ALIAS:
          emitter.alias(action.payload.eventType, action.payload.alias)
          break
        case actionTypes.EVENTS_EMIT:
          log.info(`Emitting event: ${action.payload.eventType}`, action.payload.args[0])
          emitter.emit(action.payload.eventType, ...action.payload.args)
          break
        case actionTypes.MAP_EVENTS:
          for (const actionType in action.payload) {
            if (action.payload.hasOwnProperty(actionType)) {
              const mapper = action.payload[actionType]
              if (eventMap.hasOwnProperty(actionType)) {
                eventMap[actionType].push(mapper)
              } else {
                eventMap[actionType] = [mapper]
              }
            }
          }
          break
        default:
          if (eventMap.hasOwnProperty(action.type)) {
            // Get state both before and after allowing the action to go through
            //    the reducers. This lets events have compare state changes.
            const prevState = context.getState()
            let result = next(action)
            const state = context.getState()

            // make this compatible with promise middleware by ensuring we
            // wait for the promise to resolve. It's easier to just always
            // use a promise, as opposed to handling cases.
            if (!result || !result.then) {
              result = Promise.resolve(result)
            }
            result.then(function () {
              for (const mapper of eventMap[action.type]) {
                // Use the mapper(s) for this specific event to create the event object(s).
                // Event mappings have access to the action and states pre+post reducer.
                let events = mapper(action, { prevState, state })
                if (!events) {
                  events = []
                } else if (!Array.isArray(events)) {
                  events = [events]
                }
                for (const event of events) {
                  if (typeof event !== 'undefined') {
                    if (!event.args) {
                      event.args = {}
                    }
                    context.dispatch(emitEvent(event.type, event.args))
                  }
                }
              }
            })
            return result
          }
          break
      }

      return next(action)
    }
  }
}
